import React, { useMemo, useState } from 'react';
import { format, addDays, subDays, parseISO, startOfWeek } from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar, Users, AlertTriangle } from 'lucide-react';
import { useApp } from '../context/AppContext';
import './TeamAvailability.css';

// Standalone weekly view of every active employee's schedule, hours booked,
// and approved/pending time off. The dispatcher uses this to see who has
// room to take an extra job in a given week.
function TeamAvailability() {
  const { state } = useApp();
  const [weekStart, setWeekStart] = useState(format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'));

  const weekDates = useMemo(() => {
    const start = parseISO(weekStart);
    return Array.from({ length: 7 }, (_, i) => format(addDays(start, i), 'yyyy-MM-dd'));
  }, [weekStart]);

  const activeEmployees = useMemo(
    () => state.employees.filter(e => e.status === 'active'),
    [state.employees]
  );

  const grid = useMemo(() => {
    return activeEmployees.map(emp => {
      const days = weekDates.map(date => {
        const dayJobs = state.jobs.filter(j =>
          j.date === date && (j.team || []).includes(emp.id)
        );
        const hours = dayJobs.reduce((s, j) => s + (j.duration || 0), 0);
        const off = (state.timeOffRequests || []).find(t =>
          t.employeeId === emp.id &&
          (t.status === 'approved' || t.status === 'pending') &&
          t.startDate <= date && t.endDate >= date
        );
        return { date, hours, jobCount: dayJobs.length, jobs: dayJobs, off };
      });
      const weekHours = days.reduce((s, d) => s + d.hours, 0);
      return { emp, days, weekHours };
    }).sort((a, b) => a.weekHours - b.weekHours);
  }, [activeEmployees, state.jobs, state.timeOffRequests, weekDates]);

  const prevWeek = () => setWeekStart(format(subDays(parseISO(weekStart), 7), 'yyyy-MM-dd'));
  const nextWeek = () => setWeekStart(format(addDays(parseISO(weekStart), 7), 'yyyy-MM-dd'));
  const thisWeek = () => setWeekStart(format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'));

  const totalsByDay = weekDates.map(date => {
    const offCount = grid.filter(row =>
      row.days.find(d => d.date === date)?.off
    ).length;
    const totalHours = grid.reduce(
      (s, row) => s + (row.days.find(d => d.date === date)?.hours || 0), 0
    );
    return { date, offCount, totalHours };
  });

  return (
    <div className="team-availability-page">
      <div className="team-availability-header">
        <div>
          <h1><Users size={22} /> Team Availability</h1>
          <p>Who's scheduled where this week — and where there's room.</p>
        </div>
        <div className="week-nav">
          <button className="btn btn-secondary btn-sm" onClick={prevWeek}><ChevronLeft size={16} /></button>
          <button className="btn btn-secondary btn-sm" onClick={thisWeek}>This Week</button>
          <span className="week-range">
            {format(parseISO(weekDates[0]), 'MMM d')} – {format(parseISO(weekDates[6]), 'MMM d, yyyy')}
          </span>
          <button className="btn btn-secondary btn-sm" onClick={nextWeek}><ChevronRight size={16} /></button>
        </div>
      </div>

      <div className="team-availability-grid">
        <div className="grid-corner">Employee</div>
        {weekDates.map(date => (
          <div key={date} className="grid-day-header">
            <span className="day-name">{format(parseISO(date), 'EEE')}</span>
            <span className="day-num">{format(parseISO(date), 'M/d')}</span>
          </div>
        ))}
        <div className="grid-corner">Week Total</div>

        {grid.map(row => (
          <React.Fragment key={row.emp.id}>
            <div className="grid-emp">{row.emp.firstName} {row.emp.lastName}</div>
            {row.days.map(d => {
              const loadClass = d.off ? 'off'
                : d.hours >= 8 ? 'full'
                : d.hours >= 5 ? 'busy'
                : d.hours > 0 ? 'light'
                : 'open';
              return (
                <div
                  key={d.date}
                  className={`grid-cell ${loadClass}`}
                  title={
                    d.off
                      ? `Off — ${d.off.type || 'time off'} (${d.off.status})`
                      : d.jobs.map(j => `${j.scheduledTime || ''} ${j.clientName}`).join('\n') || 'Open'
                  }
                >
                  {d.off ? (
                    <span className="cell-off">{d.off.type?.toUpperCase() || 'OFF'}</span>
                  ) : d.hours > 0 ? (
                    <>
                      <span className="cell-hours">{d.hours.toFixed(1)}h</span>
                      <span className="cell-jobs">{d.jobCount} {d.jobCount === 1 ? 'job' : 'jobs'}</span>
                    </>
                  ) : (
                    <span className="cell-open">—</span>
                  )}
                </div>
              );
            })}
            <div className="grid-total">
              <strong>{row.weekHours.toFixed(1)}h</strong>
            </div>
          </React.Fragment>
        ))}

        <div className="grid-footer-label">Daily totals</div>
        {totalsByDay.map(t => (
          <div key={t.date} className="grid-footer-cell">
            <span>{t.totalHours.toFixed(1)}h</span>
            {t.offCount > 0 && (
              <span className="off-count"><AlertTriangle size={11} /> {t.offCount} off</span>
            )}
          </div>
        ))}
        <div className="grid-footer-cell" />
      </div>

      <div className="availability-legend">
        <span className="legend-item"><span className="dot open" /> Open (0h)</span>
        <span className="legend-item"><span className="dot light" /> Light (≤5h)</span>
        <span className="legend-item"><span className="dot busy" /> Busy (5–8h)</span>
        <span className="legend-item"><span className="dot full" /> Full (≥8h)</span>
        <span className="legend-item"><span className="dot off" /> Off</span>
      </div>
    </div>
  );
}

export default TeamAvailability;
